import { createContext, useContext, type ReactNode } from 'react';
import type { Characteristic, Group } from '../schema';
import type { RuleResult } from './types';
import { EMPTY_RULE_RESULT } from './types';

const RuleResultsContext = createContext<RuleResult>(EMPTY_RULE_RESULT);

export function RuleResultsProvider({
    value,
    children,
}: {
    value: RuleResult;
    children: ReactNode;
}) {
    return <RuleResultsContext.Provider value={value}>{children}</RuleResultsContext.Provider>;
}

export function useRuleResults(): RuleResult {
    return useContext(RuleResultsContext);
}

export function useIsCharacteristicHidden(characteristicId: number): boolean {
    const { hiddenCharacteristics } = useContext(RuleResultsContext);
    return hiddenCharacteristics.has(characteristicId);
}

export function useIsValueBlacklisted(valueId: number): boolean {
    const { blacklistedValues } = useContext(RuleResultsContext);
    return blacklistedValues.has(valueId);
}

/** Errors only (warnings exposed separately). */
export function useRuleErrors() {
    const { errors } = useContext(RuleResultsContext);
    return errors;
}

/**
 * Cascade visibility — pure helpers, called both from React render paths
 * (via hooks below that snapshot the rule results) and from the validation
 * pipeline (which needs the same "is this visible right now?" answer).
 *
 * Mirrors legacy storefront rules (configento/option/abstract.js:2474-2486
 * + configurator.js:594-635:hideEmptyGroups):
 *   - Characteristic hidden when: admin set visibility hidden/backend, OR a
 *     rule blacklisted it, OR it has Values and ALL Values are blacklisted.
 *   - Group hidden when the admin ticked "hide group" (`hide: 1`, legacy
 *     storage.js:1097 — permanent; hideEmptyGroups never re-shows it), OR
 *     no Characteristic in it is visible.
 *
 * "Hidden" here is the customer-facing condition; backend/admin code still
 * sees every Characteristic in the parsed Component as before.
 */
export function isCharacteristicVisible(
    char: Characteristic,
    rules: { hiddenCharacteristics: ReadonlySet<number>; blacklistedValues: ReadonlySet<number> },
): boolean {
    if (rules.hiddenCharacteristics.has(char.id)) return false;
    if (char.visibility === 'hidden' || char.visibility === 'backend') return false;
    if ('values' in char && Array.isArray(char.values) && char.values.length > 0) {
        const hasUnblacklistedValue = char.values.some((v) => !rules.blacklistedValues.has(v.id));
        if (!hasUnblacklistedValue) return false;
    }
    return true;
}

export function isGroupVisible(
    group: Group,
    rules: { hiddenCharacteristics: ReadonlySet<number>; blacklistedValues: ReadonlySet<number> },
): boolean {
    if (group.hidden) return false;
    return group.characteristics.some((c) => isCharacteristicVisible(c, rules));
}

/** Hook variants that snapshot the current RuleResults context. */
export function useIsCharacteristicVisible(char: Characteristic): boolean {
    return isCharacteristicVisible(char, useContext(RuleResultsContext));
}

export function useIsGroupVisible(group: Group): boolean {
    return isGroupVisible(group, useContext(RuleResultsContext));
}
